// ─── Section 105 Plan Advisor — Decision Engine + Eligibility Bridge ───
import { SEED_105_PLAN_TYPES, SEED_105_PACKAGES, SEED_105_COVERAGE_LAYERS } from '../data/section105Defaults';
import { optimizeEmployeeWithEligibility } from './calculations';

/**
 * Given the 6 wizard answers, return the recommended plan type id.
 * Mirrors the decision tree in the product spec.
 */
export function getRecommendedPlanId(answers) {
  if (!answers) return 'integrated_merp';
  const { size, groupHealth, goal, budget, timeline } = answers; // eslint-disable-line no-unused-vars

  // Path 1: Small + no group health → QSEHRA
  if (size === 'under50' && groupHealth === 'no') return 'qsehra';

  // Path 2: Has group health + supplement/combine + low budget → Excepted Benefit HRA
  if (groupHealth === 'yes' && (goal === 'supplement' || goal === 'combine125') && budget === 'low') {
    return 'excepted_benefit';
  }

  // Path 3: 50+ + no/dropping group + replace/high budget → ICHRA
  if (size === '50plus' &&
      (groupHealth === 'no' || groupHealth === 'considering') &&
      (goal === 'replace' || budget === 'high' || budget === 'premium')) {
    return 'ichra';
  }

  // Path 4: Considering dropping group + high budget → ICHRA
  if (groupHealth === 'considering' && (budget === 'high' || budget === 'premium')) {
    return 'ichra';
  }

  // Path 5: Has group health + supplement + mid+ budget → Group Coverage HRA
  if (groupHealth === 'yes' && goal === 'supplement' && budget !== 'low') {
    return 'gchra';
  }

  // Default: Integrated MERP (best for 125 combination)
  return 'integrated_merp';
}

/**
 * Look up the full plan type definition by id, falling back to the seed if admin hasn't customized it.
 */
export function getPlanTypeById(id, adminPlanTypes) {
  const list = Array.isArray(adminPlanTypes) && adminPlanTypes.length > 0 ? adminPlanTypes : SEED_105_PLAN_TYPES;
  return list.find(p => p.id === id) || null;
}

/**
 * Translate budget answer id → dollar amount per month.
 */
export function budgetToMonthlyAmount(budgetId) {
  const map = { low: 100, mid: 200, high: 450, premium: 700 };
  return map[budgetId] || 100;
}

/**
 * Compute the "eligibility bridge": re-run optimization with an HRA contribution
 * simulated as an equivalent salary boost (on a best-effort basis). Returns counts.
 *
 * @param {object} params
 *   employees: raw census employees
 *   analyzed: existing analyzed employees (with optimal/hasViable set by optimizeEmployeeWithEligibility)
 *   config: engine config
 *   monthlyContribution: $ per employee per month
 */
export function computeEligibilityBridge({ employees, analyzed, config, monthlyContribution }) {
  const total = employees?.length || analyzed?.length || 0;
  const currentEligible = (analyzed || []).filter(a => a.hasViable).length;
  const annualContribution = (Number(monthlyContribution) || 0) * 12;

  // Simulate the HRA as a salary boost of the same annual amount.
  // This approximates how an additional tax-advantaged contribution brings
  // previously sub-threshold employees over the break-even point.
  let bridgedCount = 0;
  let bridgeFicaSavings = 0;
  (analyzed || []).forEach(a => {
    if (a.hasViable) return; // already eligible
    const originalSalary = Number(a.salary) || 0;
    if (!originalSalary) return;
    const simulated = optimizeEmployeeWithEligibility(
      { ...a, salary: originalSalary + annualContribution },
      config
    );
    if (simulated.hasViable && simulated.optimal) {
      bridgedCount += 1;
      bridgeFicaSavings += simulated.optimal.employerAnnualFica || 0;
    }
  });

  const newEligible = currentEligible + bridgedCount;
  return {
    totalEmployees: total,
    currentEligible,
    bridgedCount,
    newEligible,
    currentPct: total > 0 ? currentEligible / total : 0,
    newPct: total > 0 ? newEligible / total : 0,
    bridgeFicaSavings: Math.round(bridgeFicaSavings),
    annualContribution,
    employerAnnualCost: annualContribution * bridgedCount,
    netAnnualCost: Math.max(0, annualContribution * bridgedCount - bridgeFicaSavings),
  };
}

/**
 * Pre-fill wizard answers from a client record when possible.
 */
export function prefillAnswersFromClient(client) {
  const answers = {};
  const employees = client?.employees || [];
  if (employees.length > 0) {
    const fte = employees.filter(e => (Number(e.weeklyHours || e.hoursPerWeek || 40) >= 30)).length;
    answers.size = fte >= 50 ? '50plus' : 'under50';
  }
  // Group health is not currently tracked in client schema — leave blank
  return answers;
}

/**
 * Render a markdown document template by substituting {merge_field} placeholders.
 */
export function renderDocumentTemplate(template, mergeValues) {
  if (!template) return '';
  let out = template;
  Object.entries(mergeValues || {}).forEach(([k, v]) => {
    out = out.split('{' + k + '}').join(String(v ?? ''));
  });
  return out;
}

/**
 * Build the merge-field value map from a client + wizard answers + bridge result.
 */
export function buildMergeFields(client, answers, bridge) {
  const today = new Date();
  const yearStart = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  return {
    company_name: client?.company?.legalName || client?.company?.dba || 'Your Company',
    effective_date: yearStart.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    administrator_name: 'HealthCues, as Plan Administrator',
    contribution_amount: String(budgetToMonthlyAmount(answers?.budget)),
    eligible_classes: 'All full-time employees averaging 30 or more hours per week',
    eligibility_waiting_period: 'First of the month following 30 days of employment',
    plan_year_start: yearStart.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    class_contributions: 'See attached schedule by employee class',
    bridged_count: String(bridge?.bridgedCount ?? 0),
    bridge_savings: (bridge?.bridgeFicaSavings ?? 0).toLocaleString('en-US'),
  };
}


// ─── Package Recommendation Engine ──────────────────────────────
// Returns a primary package id + 1-2 alternative ids based on wizard answers.
// Mirrors the spec decision tree (size × group health × goal × budget).
export function getPackageRecommendations(answers) {
  if (!answers) return { primary: 'integrated_merp', alternatives: [] };
  const { size, groupHealth, goal, budget } = answers;
  let primary;
  let alternatives = [];

  // Small employer, no group health
  if (size === 'under50' && groupHealth === 'no') {
    if (budget === 'high' || budget === 'premium') {
      primary = 'ichra';
      alternatives = ['qsehra_mec', 'integrated_merp'];
    } else {
      primary = 'qsehra_mec';
      alternatives = ['qsehra_healthshare', 'integrated_merp'];
    }
  }
  // Has group health → supplement
  else if (groupHealth === 'yes') {
    if (budget === 'low') {
      primary = 'excepted_benefit';
      alternatives = ['gchra', 'integrated_merp'];
    } else {
      primary = 'gchra';
      alternatives = ['excepted_benefit', 'integrated_merp'];
    }
  }
  // 50+ dropping group health
  else if (size === '50plus' && groupHealth === 'considering') {
    primary = 'ichra';
    alternatives = ['integrated_merp_mec', 'qsehra_mec'];
  }
  // 50+, no group health
  else if (size === '50plus' && groupHealth === 'no') {
    primary = 'ichra';
    alternatives = ['integrated_merp_mec'];
  }
  // Combine with 125 as primary goal
  else if (goal === 'combine125') {
    if (size === '50plus') {
      primary = 'integrated_merp_mec';
      alternatives = ['ichra', 'integrated_merp_healthshare'];
    } else {
      primary = 'integrated_merp';
      alternatives = ['qsehra_mec', 'integrated_merp_healthshare'];
    }
  }
  // Default fallback
  else {
    if (size === '50plus') {
      primary = 'integrated_merp_mec';
      alternatives = ['ichra', 'integrated_merp_healthshare'];
    } else {
      primary = 'integrated_merp';
      alternatives = ['qsehra_mec', 'integrated_merp_healthshare'];
    }
  }

  // Dedupe and trim to 2 alternatives
  alternatives = Array.from(new Set(alternatives.filter(id => id && id !== primary))).slice(0, 2);
  return { primary, alternatives };
}

/**
 * Look up the full package definition by id, falling back to seed if admin
 * hasn't customized it. Hydrates the package with linked plan-type + coverage-
 * layer references for the UI.
 */
export function getPackageById(id, adminPackages, adminPlanTypes, adminCoverageLayers) {
  const list = Array.isArray(adminPackages) && adminPackages.length > 0 ? adminPackages : SEED_105_PACKAGES;
  const pkg = list.find(p => p.id === id);
  if (!pkg) return null;
  const planType = getPlanTypeById(pkg.section105Type, adminPlanTypes);
  const coverage = pkg.coverageLayer ? getCoverageLayerById(pkg.coverageLayer, adminCoverageLayers) : null;
  return { ...pkg, _planType: planType, _coverage: coverage };
}

export function getCoverageLayerById(id, adminCoverageLayers) {
  if (!id) return null;
  const map = adminCoverageLayers && typeof adminCoverageLayers === 'object'
    ? adminCoverageLayers
    : SEED_105_COVERAGE_LAYERS;
  return map[id] || SEED_105_COVERAGE_LAYERS[id] || null;
}

/**
 * Aggregate a package's monthly cost using admin-editable coverage cost
 * estimates rather than the package's hard-coded `estMonthlyEmployerCost`.
 * Returns { monthly, range, parts } where parts is a breakdown.
 */
export function computePackageCost(pkg, adminCoverageLayers) {
  if (!pkg) return { monthly: 0, range: [0, 0], parts: [] };
  const coverage = pkg.coverageLayer ? getCoverageLayerById(pkg.coverageLayer, adminCoverageLayers) : null;
  const parts = [];
  // 105 plan portion — pulled from admin estimate baked into the package
  // (planMonthlyCostEstimate would be admin-editable; default to package est minus coverage cost)
  const coverageMonthly = coverage ? Number(coverage.monthlyCostEstimate || 0) : 0;
  const planMonthly = Math.max(0, Number(pkg.estMonthlyEmployerCost || 0) - coverageMonthly);
  parts.push({ label: pkg.section105Type ? pkg.section105Type.toUpperCase() : 'Plan', amount: planMonthly });
  if (coverage) {
    parts.push({ label: coverage.name, amount: coverageMonthly });
  }
  const monthly = planMonthly + coverageMonthly;
  const lowOffset = (pkg.employerCostRange && pkg.employerCostRange[0]) || monthly;
  const highOffset = (pkg.employerCostRange && pkg.employerCostRange[1]) || monthly;
  return { monthly, range: [lowOffset, highOffset], parts };
}

/**
 * Build a list of package-level deliverables, combining 105 plan deliverables
 * with coverage-layer specific docs.
 */
export function getPackageDeliverables(pkg) {
  if (!pkg) return [];
  const planDocs = (pkg._planType && pkg._planType.deliverables) || [];
  const out = planDocs.map(d => ({ ...d, group: pkg._planType?.type || 'Plan' }));

  if (pkg.coverageLayer === 'mec') {
    out.push(
      { id: 'mec_summary', title: 'MEC Plan Summary', description: 'Employee-facing coverage summary', group: 'MEC' },
      { id: 'mec_enrollment', title: 'MEC Enrollment Forms', description: 'Employee election + dependent forms', group: 'MEC' },
      { id: 'mec_1095_guide', title: 'Form 1095-B/C Reporting Guide', description: 'ACA reporting walkthrough', group: 'MEC' },
      { id: 'mec_aca_checklist', title: 'ACA Compliance Checklist', description: 'Employer mandate adherence steps', group: 'MEC' },
    );
  }
  if (pkg.coverageLayer === 'health_share') {
    out.push(
      { id: 'hs_enrollment_guide', title: 'Health Share Enrollment Guide', description: 'How employees join the ministry', group: 'Health Share' },
      { id: 'hs_state_compliance', title: 'State Regulatory Compliance Review', description: 'Per-state legal review checklist', group: 'Health Share' },
      { id: 'hs_legal_review_request', title: 'Legal Review Request Template', description: '⚠ Required before implementation', group: 'Health Share' },
      { id: 'hs_employee_disclosure', title: 'Employee Disclosure: Not Insurance', description: 'Required transparency document', group: 'Health Share' },
      { id: 'hs_mec_gap_analysis', title: 'MEC Gap Analysis', description: 'Which employees still need MEC', group: 'Health Share' },
    );
  }

  // Always include package-level combined deliverables
  out.push(
    { id: 'package_combined_calculator', title: 'Combined 125 + 105 + Coverage Calculator', description: 'Full package value analysis', group: 'Package' },
    { id: 'package_eligibility_bridge', title: 'Eligibility Bridge Analysis', description: 'Who moves from ineligible to eligible', group: 'Package' },
    { id: 'package_compliance_checklist', title: 'Full Compliance Checklist', description: 'All layers — admin and timing', group: 'Package' },
    { id: 'package_deck', title: 'Client Presentation Deck', description: 'Package-level slide deck', group: 'Package' },
    { id: 'package_talking_points', title: 'Package Talking Points', description: 'AM-ready conversation guide', group: 'Package' },
  );
  return out;
}
